import { useId } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
import { Checkbox } from '@shared/components';
import { AMOUNT_BASIS_LABELS } from '../utils/amountBasis';
import type { AmountBasis } from '../utils/amountBasis';
import type { SelectionUnit } from '../utils/invoiceSelection';
import type { SelectionTone, ServiceSelectionRow } from '../utils/selectionTableRows';
import { DueDateLabel } from './DueDateLabel';
import { SoldServiceItemsPanel } from './SoldServiceItemsPanel';

export const SERVICE_ROW_GRID = 'grid grid-cols-[2.5rem_minmax(0,1fr)]';
export const SERVICE_BUTTON_GRID =
    'grid grid-cols-[1rem_minmax(0,1fr)_auto] gap-x-3 gap-y-0.5 @min-[28rem]:grid-cols-[1rem_minmax(0,1fr)_minmax(6.5rem,auto)_minmax(6.5rem,auto)]';
export const SERVICE_DUE_CELL = 'col-start-2 row-start-2 @min-[28rem]:col-start-3 @min-[28rem]:row-start-1';
export const SERVICE_NET_CELL = 'col-start-3 row-start-1 @min-[28rem]:col-start-4';

interface Props {
    row: ServiceSelectionRow;
    expanded: boolean;
    onToggleExpand: (serviceId: string) => void;
    flexible: boolean;
    selectedKeys: ReadonlySet<string>;
    onToggle: (unit: SelectionUnit) => void;
    locked: boolean;
    basis?: AmountBasis;
    readOnlyItems?: boolean;
}

const rowClass = (tone: SelectionTone, expanded: boolean): string => {
    if (tone === 'selected') return 'bg-mp-primary/10';
    if (tone === 'partial') return 'bg-mp-primary/5';
    return expanded ? 'bg-mp-background' : 'hover:bg-mp-background';
};

export function SoldServiceRow({ row, expanded, onToggleExpand, flexible, selectedKeys, onToggle, locked, basis = 'net', readOnlyItems = false }: Props) {
    const { service, checkbox, due } = row;
    const baseId = useId();
    const ids = { name: `${baseId}-name`, note: `${baseId}-note`, selection: `${baseId}-selection`, due: `${baseId}-due`, net: `${baseId}-net` };
    const hasDue = due.date !== null || due.mileage !== null;
    const describedBy = [
        row.note && ids.note,
        row.selectionNote && ids.selection,
        hasDue && ids.due,
        ids.net,
    ].filter(Boolean).join(' ');

    return (
        <li className={`${SERVICE_ROW_GRID} ${rowClass(row.tone, expanded)}`}>
            <span className="flex items-start justify-center pt-3">
                <Checkbox
                    ariaLabel={`Select ${service.name}`}
                    checked={checkbox.checked}
                    disabled={locked || checkbox.disabled}
                    ariaDescribedBy={row.note ? ids.note : undefined}
                    onChange={() => onToggle({ kind: 'service', serviceId: service.id })}
                />
            </span>
            <button
                type="button"
                aria-labelledby={ids.name}
                aria-describedby={describedBy}
                aria-expanded={expanded}
                aria-controls={expanded ? `sold-service-${service.id}-items` : undefined}
                onClick={() => onToggleExpand(service.id)}
                className={`${SERVICE_BUTTON_GRID} min-h-11 w-full items-start py-2 pr-3 text-left text-sm focus-visible:outline-2 focus-visible:outline-mp-link`}
            >
                <span aria-hidden="true" className="col-start-1 row-start-1 pt-0.5">
                    {expanded ? <ChevronDownIcon className="size-4" /> : <ChevronRightIcon className="size-4" />}
                </span>
                <span className="col-start-2 row-start-1 min-w-0 break-words">
                    <span id={ids.name} className="block text-mp-foreground">{service.name}</span>
                    {row.note && <span id={ids.note} className="block text-xs text-mp-muted">{row.note}</span>}
                    {row.selectionNote && <span id={ids.selection} className="block text-xs text-mp-muted">{row.selectionNote}</span>}
                </span>
                <span id={ids.due} className={`${SERVICE_DUE_CELL} block text-mp-muted`}>
                    {due.date && <DueDateLabel date={due.date} overdue={row.overdue} className="inline-flex text-sm" />}
                    {due.mileage && <span className="block text-xs">{due.mileage}</span>}
                    {!hasDue && '—'}
                </span>
                <span id={ids.net} className={`${SERVICE_NET_CELL} block text-right tabular-nums text-mp-foreground`}>
                    <span className="sr-only">{AMOUNT_BASIS_LABELS[basis].column}</span>{' '}
                    <span className="block">{row.net}</span>
                    {row.fullNet && <> <span className="block text-xs text-mp-muted">{row.fullNet}</span></>}
                </span>
            </button>
            {expanded && (
                <div className="col-span-2 pb-3 pl-3 pr-3 @min-[28rem]:pl-10">
                    <SoldServiceItemsPanel
                        service={service}
                        flexible={flexible}
                        selectedKeys={selectedKeys}
                        onToggle={onToggle}
                        locked={locked}
                        basis={basis}
                        readOnlyItems={readOnlyItems}
                    />
                </div>
            )}
        </li>
    );
}
